import { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import useFormSubmit from '@/hooks/useFormSubmit';
import { site } from '@/mocks/site';
import { categories } from '@/mocks/categories';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dahghproh653ivfvnuig';

export default function Footer() {
  const { status, message, submit } = useFormSubmit();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit(event.currentTarget, FORM_ENDPOINT);
  };

  return (
    <footer className="bg-secondary-900 text-secondary-50">
      <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-lg font-bold text-background-50">
                蝌
              </span>
              <span className="font-heading text-xl font-bold">蝌蚪吃瓜</span>
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-secondary-100/80">{site.intro}</p>
            <div className="mt-6">
              <h3 className="font-heading text-base font-bold">订阅热点更新</h3>
              <p className="mt-1 text-xs text-secondary-100/70">
                每周一封邮件，汇总本周最值得看的三条时间线。
              </p>
              <form
                id="kedou-newsletter-form"
                data-readdy-form
                action={FORM_ENDPOINT}
                method="post"
                onSubmit={handleSubmit}
                className="mt-3 flex flex-col gap-2 sm:flex-row"
              >
                <label htmlFor="newsletter-email" className="sr-only">
                  邮箱地址
                </label>
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  required
                  placeholder="你的邮箱地址"
                  autoComplete="email"
                  className="w-full rounded-md border border-secondary-700 bg-secondary-800 px-3 py-2.5 text-sm text-secondary-50 placeholder:text-secondary-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                />
                <input
                  className="form-extra-note"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {status === 'submitting' ? '提交中…' : '立即订阅'}
                </button>
              </form>
              {status === 'success' && (
                <p className="mt-2 text-xs text-accent-300">{message}</p>
              )}
              {status === 'error' && (
                <p className="mt-2 text-xs text-primary-300">{message}</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-heading text-sm font-bold tracking-wide text-secondary-50">栏目分类</h3>
            <ul className="mt-4 space-y-2.5">
              {categories.slice(0, 5).map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={`/timeline?category=${cat.id}`}
                    className="text-sm text-secondary-100/80 transition-colors hover:text-primary-300"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-heading text-sm font-bold tracking-wide text-secondary-50">站点导航</h3>
            <ul className="mt-4 space-y-2.5">
              <li>
                <Link to="/" className="text-sm text-secondary-100/80 transition-colors hover:text-primary-300">
                  首页
                </Link>
              </li>
              <li>
                <Link to="/timeline" className="text-sm text-secondary-100/80 transition-colors hover:text-primary-300">
                  事件合集
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-secondary-100/80 transition-colors hover:text-primary-300">
                  关于我们
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-secondary-100/80 transition-colors hover:text-primary-300">
                  联系我们
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-heading text-sm font-bold tracking-wide text-secondary-50">联系方式</h3>
            <ul className="mt-4 space-y-3 text-sm text-secondary-100/80">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-300" />
                <a href={`mailto:${site.email}`} className="transition-colors hover:text-primary-300">
                  {site.email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-mail-send-line text-primary-300" />
                <a href={`mailto:${site.cooperateEmail}`} className="transition-colors hover:text-primary-300">
                  {site.cooperateEmail}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-time-line mt-0.5 text-primary-300" />
                <span>内容最后更新：{site.updateTime}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-secondary-700 pt-6 text-xs text-secondary-200/70 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} 蝌蚪吃瓜 · 娱乐事件时间线复盘</p>
          <p>站内案例为编辑整理的话题复盘，人物与作品名称使用站内叙事代号。</p>
        </div>
      </div>
    </footer>
  );
}